import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    titleText = "titleText",
    bodyTextLines = "bodyTextLines",
    bgImageUrl = "bgImageUrl",
    buttonText = "buttonText",
    buttonLink = "buttonLink",
    buttonColor = "buttonColor",
    customCSS = "customCSS",
}

interface BodyTextLineModel {
    fields: {
        line: string;
    };
}

interface OwnProps {
    extendedStyles: WidgetAlphaStyles;
    fields: {
        [fields.titleText]: string;
        [fields.bodyTextLines]: BodyTextLineModel[];
        [fields.bgImageUrl]: string;
        [fields.buttonText]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonColor]: string;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps;

export interface WidgetAlphaStyles {
    wrapper?: InjectableCss;
    mainPanel?: InjectableCss;
    imagePanel?: InjectableCss;
    image?: LazyImageProps;
    clickableStyles?: ClickablePresentationProps;
    topSubPanel?: InjectableCss;
    topSubPanelTextWrapper?: InjectableCss;
    topSubPanelText?: TypographyPresentationProps;
    bottomSubPanel?: InjectableCss;
    bottomSubPanelTextWrapper?: InjectableCss;
    bottomSubPanelText?: TypographyPresentationProps;
    buttonPanel?: InjectableCss;
    buttonWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const widgetAlphaStyles: WidgetAlphaStyles = {
    wrapper: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    mainPanel: {
        css: css`
            width: 100%;
            height: auto;
            position: relative;
        `,
    },
    imagePanel: {
        css: css`
            width: 100%;
            height: auto;
        `,
    },
    clickableStyles: {
        css: css`
            display: block;
        `,
    },
    topSubPanel: {
        css: css`
            width: 100%;
        `,
    },
    topSubPanelTextWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 80px;
            background: #cfcece;
        `,
    },
    topSubPanelText: {
        css: css`
            text-transform: uppercase;
            letter-spacing: 0.06em;
            font-weight: 700;
            color: black;
            font-size: 20px;
        `,
    },
    bottomSubPanel: {
        css: css`
            width: 100%;
            padding-bottom: 12px;
            padding-top: 20px;
            font-size: 20px;
            max-width: 70%;
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    bottomSubPanelTextWrapper: {
        css: css``,
    },
    bottomSubPanelText: {
        css: css`
            color: black;
        `,
    },
    buttonPanel: {
        css: css`
            width: 100%;
            margin-top: auto;
        `,
    },
    buttonWrapper: {
        css: css`
            width: 100%;
        `,
    },
    button: {
        css: css`
            width: 70%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
        typographyProps: {
            color: "black",
            size: 12,
            css: css`
                text-transform: uppercase;
                font-weight: 800;
            `,
        },
    },
};

const WidgetAlpha: FC<Props> = props => {
    const { extendedStyles, fields } = props;
    const { titleText, bodyTextLines, bgImageUrl, buttonText, buttonLink, buttonColor, customCSS } = fields;

    const [styles] = React.useState(() => mergeToNew(widgetAlphaStyles, extendedStyles));

    const history = useHistory();
    const { url } = useGetLink(buttonLink);
    const { isInShell } = useShellContext();

    const handleButtonClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const customCssWrapper = {
        css: css`
            height: 100%;
            ${customCSS}
        `,
    };

    const buttonStyle = {
        ...styles.button,
    };

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="widgetAlpha_customCssWrapper">
            <StyledWrapper {...styles.wrapper} className="wrapper" data-test-selector="wrapper">
                <StyledWrapper {...styles.mainPanel} data-test-selector="mainPanel">
                    <Clickable onClick={handleButtonClick} {...styles.clickableStyles}>
                        <StyledWrapper {...styles.topSubPanel} data-test-selector="topSubPanel">
                            <StyledWrapper {...styles.topSubPanelTextWrapper}>
                                <Typography {...styles.topSubPanelText}>{titleText}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.imagePanel} data-test-selector="imagePanel">
                            <LazyImage src={bgImageUrl} {...styles.image} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.bottomSubPanel} data-test-selector="bottomSubPanel">
                            {bodyTextLines &&
                                bodyTextLines.map(item => (
                                    <StyledWrapper {...styles.bottomSubPanelTextWrapper} key={item.fields.line}>
                                        <Typography {...styles.bottomSubPanelText}>{item.fields.line}</Typography>
                                    </StyledWrapper>
                                ))}
                        </StyledWrapper>
                    </Clickable>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonPanel}>
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button {...buttonStyle} onClick={handleButtonClick}>
                            {buttonText}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {

}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 1,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: WidgetAlpha,
    definition: {
        group: "Basic",
        displayName: "Widget Aplha",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.titleText,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.bodyTextLines,
                displayName: "Body Text",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.line;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "line",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
                tab: basicTab,
                sortOrder: 20,
            },
            {
                name: fields.bgImageUrl,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 30,
            },
            {
                name: fields.buttonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonLink,
                fieldType: "General",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: buttonTab,
                sortOrder: 120,
            },
            {
                name: fields.buttonColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                tab: buttonTab,
                sortOrder: 130,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                sortOrder: 200,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
